// @ts-strict-ignore
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { type RefObject, type SyntheticEvent } from "react";

import { Link } from "@aviary";
import { useDropdownContext } from "@aviary/components/Dropdown/DropdownContext";
import { CategoryLink } from "@emerson/components/CategoryLink/CategoryLink";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import type { MenuCategory, MenuSubCategory } from "@shared/types/graphqlGenerated";

import * as styles from "./MenuItem.styles";

interface Props {
  isActive?: boolean;
  text?: string;
  to?: string;
  hasSubMenu?: boolean;
  isMainCategory?: boolean;
  menuCategory?: MenuCategory | MenuSubCategory;
  isSideMenuOpen: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
  onMouseEnter?: (e: SyntheticEvent, menuCategory: MenuCategory | MenuSubCategory) => void;
  onMouseLeave?: () => void;
  onFocus?: (menuCategory?: MenuCategory | MenuSubCategory) => void;
  onBlur?: () => void;
  onClick?: (event: React.MouseEvent) => void;
  parentCategoryName?: string;
  isExternalLink?: boolean;
  ref?: RefObject<HTMLDivElement>;
}

const MenuItem = ({
  ref,
  to,
  text,
  isActive,
  isSideMenuOpen,
  hasSubMenu,
  isMainCategory,
  menuCategory,
  onKeyDown,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  onClick,
  parentCategoryName,
  isExternalLink,
}: Props) => {
  const { onItemSelected, setItemFocused } = useDropdownContext();
  const menuItemStyles = [styles.base, isMainCategory && styles.mainCategory];

  const linkProps = isExternalLink ? { href: to } : { to };

  const clickHandler = (e?: SyntheticEvent) => {
    selectHandler(e);
  };

  const getOptions = () => ({
    closeOnItemSelect: true,
  });

  const selectHandler = e => {
    onItemSelected(() => onClick?.(e), getOptions());
  };

  const keyDownHandler = (event: React.KeyboardEvent<HTMLElement>) => {
    onKeyDown?.(event);
    if (event.key === "Enter") {
      selectHandler(event);
    }
  };

  const handleFocus = () => {
    onFocus?.(menuCategory);
    setItemFocused(true);
  };

  const renderMenuItem = () =>
    to ? (
      <Link
        isColor="system"
        {...linkProps}
        isUnderLined={false}
        css={[...menuItemStyles, styles.bold]}
        aria-label={text}
        noExternalIcon
      >
        {text}
        {!!hasSubMenu && <FontAwesomeIcon icon={faChevronRight} fontSize={8} />}
      </Link>
    ) : (
      <div css={[...menuItemStyles, styles.bold]} tabIndex={0}>
        {text}
        {!!hasSubMenu && <FontAwesomeIcon icon={faChevronRight} fontSize={8} />}
      </div>
    );

  const content = menuCategory ? (
    <CategoryLink
      category={menuCategory}
      categoryName={menuCategory.name}
      linkStyles={menuItemStyles}
      hasOnlyOneSubcategory={!hasSubMenu}
      isMarginless
    >
      {!!hasSubMenu && <FontAwesomeIcon icon={faChevronRight} fontSize={8} />}
    </CategoryLink>
  ) : (
    renderMenuItem()
  );

  if (isMainCategory) {
    return (
      <div
        css={styles.wrapper}
        role="menuitem"
        tabIndex={1}
        onKeyDown={keyDownHandler}
        onMouseEnter={e => onMouseEnter?.(e, menuCategory)}
        onMouseLeave={onMouseLeave}
        onFocus={handleFocus}
        onBlur={onBlur}
        onClick={clickHandler}
        ref={ref}
        aria-expanded={!!isSideMenuOpen && isActive}
      >
        {content}
      </div>
    );
  }

  return (
    <CategoryLink
      category={menuCategory}
      categoryName={parentCategoryName}
      linkStyles={menuItemStyles}
      isSubcategory
      isHoverUnderLined
      isMarginless
      role="menuitem"
      sideMenuCallbacks={{
        onFocus: () => onFocus(menuCategory),
        onBlur,
      }}
      onClick={onClick}
    />
  );
};

export { MenuItem };
